/* eslint-disable react/no-array-index-key */

import {
    getPrePurchaseProductPriceGroups,
    prePurchaseProductPricePresentationLogic,
} from '../../../../../cart/components/molecules/PrepurchaseProductPriceLabels/PrepurchaseProductPriceLabels';
import type { A11yTextType } from '../../../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type { PriceType } from '../../../../../common/models/priceData/priceData';
import type { ProductVariantProps } from '../../../../../common/models/props/itemTeaserProps';
import renderProductPriceGroups, {
    defineProductPriceGroup,
} from '../../../../utils/renderProductPriceGroups';
import { getHistoricalPriceText } from '../../../ProductInfoProperty/ProductInfoProperty.utils';
import ProductDetailsHistoricalPrice from '../ProductDetailsHistoricalPrice';
import type { ProductDetailsData } from '../ProductDetailsPage.types';

export const getProductDetailsPriceLabelA11yText = (params: Params): string => {
    const productDetailsSecondaryPriceLabels = getPriceGroups(params);
    return productDetailsSecondaryPriceLabels[params.type].a11y;
};

const ProductDetailsPriceLabels = (props: Params) => {
    const productDetailsSecondaryPriceLabels = getPriceGroups(props);
    return productDetailsSecondaryPriceLabels[props.type].render;
};

export default ProductDetailsPriceLabels;

type Params = {
    product: ProductDetailsData;
    variant: ProductVariantProps | undefined;
    type: 'primary' | 'secondary';
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isUserMember: boolean;
    isPILPricesLogicEnabled: boolean;
    arePILPricesEnabled: boolean;
    isB2B: boolean;
};

const getPriceGroups = (params: Params) => {
    const base = getPrePurchaseProductPriceGroups(params);

    return renderProductPriceGroups(
        () => `${params.product.identifier}_${params.variant?.id}`,
        [
            defineProductPriceGroup({
                type: 'primary',
                specSet: ['primaryPrice'],
                createLabel: base.definitions.primary.createLabel,
            }),
            defineProductPriceGroup({
                type: 'secondary',
                specSet: [
                    'maxPromoUsage',
                    'recycleFee',
                    'promoComparativePrice',
                    'originalPrice',
                    'comparativePrice',
                    'historicalPrice',
                    'excludingTax',
                ],
                createLabel: (spec) => {
                    switch (spec) {
                        case 'historicalPrice': {
                            return prePurchaseProductPricePresentationLogic.getSecondaryPricePresentation(
                                {
                                    product: params.product,
                                    priceType: params.priceType,
                                    a11yTextType: params.a11yTextType,
                                    isB2B: params.isB2B,
                                    isUserMember: params.isUserMember,
                                    arePILPricesEnabled: params.arePILPricesEnabled,
                                    isPILPricesLogicEnabled: params.isPILPricesLogicEnabled,
                                },
                            )(spec, {
                                historicalPrice: (_params) => {
                                    // we need to override since PDP should have it clickable and underlined
                                    const texts = getHistoricalPriceText(
                                        params.product,
                                        params.priceType,
                                    );
                                    if (!texts?.visual) return null;

                                    return {
                                        a11y: texts.a11y,
                                        render: () => (
                                            <ProductDetailsHistoricalPrice
                                                productIdentifier={params.product.identifier}
                                                visualText={texts.visual}
                                                a11yText={texts.a11y}
                                                a11yType={params.a11yTextType}
                                            />
                                        ),
                                    };
                                },
                            });
                        }
                        default:
                            return base.definitions.secondary.createLabel(spec);
                    }
                },
            }),
        ],
    );
};
